"use client";

import React, { useState } from "react";
import Image from "next/image";
import { Title, Description } from "@/components/Common/Typography";
import { MapPin, Maximize2, Factory } from "lucide-react";
import styles from "../export.module.css";

export interface FactoryItem {
  num: string;
  name: string;
  address: string;
  area: string;
  capacity: string;
  images: string[];
}

export interface FactorySectionProps {
  data?: {
    subtitle?: string;
    title: string;
    items: FactoryItem[];
  };
  locale: string;
}

export default function FactorySection({ data, locale }: FactorySectionProps) {
  const [selectedGallery, setSelectedGallery] = useState<{ [key: number]: number }>({
    0: 0,
    1: 0,
    2: 0,
  });

  if (!data || !data.items || data.items.length === 0) return null;

  const handleThumbnailClick = (factoryIdx: number, imgIdx: number) => {
    setSelectedGallery((prev) => ({
      ...prev,
      [factoryIdx]: imgIdx,
    }));
  };

  return (
    <section className={styles.factorySection}>
      <div className={styles.container}>
        <div className={styles.factoryHeader}>
          <Title
            as="h2"
            size="medium"
            align="center"
            className={styles.sectionTitle}
          >
            {data.title}
          </Title>
        </div>

        <div className={styles.factoryGrid}>
          {data.items.map((factory, factoryIdx) => {
            const activeImgIdx = selectedGallery[factoryIdx] || 0;
            const activeImgId = factory.images[activeImgIdx];

            return (
              <div key={factoryIdx} className={styles.factoryCard}>
                <div className={styles.cardHeader}>
                  <Title
                    as="h3"
                    size="small"
                    className={styles.cardTitle}
                  >
                    {factory.name}
                  </Title>
                  <span className={styles.cardNumber}>{factory.num}</span>
                </div>

                {/* Factory Main Image Area */}
                <div className={styles.cardImageArea}>
                  {activeImgId && (
                    <Image
                      src={activeImgId}
                      alt={`${factory.name} - View ${activeImgIdx + 1}`}
                      fill
                      sizes="(max-width: 768px) 100vw, 30vw"
                      className={styles.cardImage}
                    />
                  )}
                </div>

                {/* Gallery Thumbnails */}
                <div className={styles.thumbnailRow}>
                  {factory.images.map((imgId, imgIdx) => (
                    <div
                      key={imgIdx}
                      className={`${styles.thumbnail} ${
                        activeImgIdx === imgIdx ? styles.activeThumbnail : ""
                      }`}
                      onClick={() => handleThumbnailClick(factoryIdx, imgIdx)}
                    >
                      {imgId && (
                        <Image
                          src={imgId}
                          alt={`${factory.name} thumbnail ${imgIdx + 1}`}
                          fill
                          sizes="48px"
                          className={styles.thumbnailImage}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Factory Details */}
                <div className={styles.cardDetails}>
                  <div className={styles.detailItem}>
                    <MapPin className={styles.detailIcon} size={18} />
                    <div className={styles.detailContent}>
                      <span className={styles.detailLabel}>
                        {locale === "vi" ? "Địa chỉ:" : "Address:"}
                      </span>
                      <Description
                        as="span"
                        size="medium"
                        className={styles.detailValue}
                        translate="no"
                      >
                        {factory.address}
                      </Description>
                    </div>
                  </div>

                  <div className={styles.detailItem}>
                    <Maximize2 className={styles.detailIcon} size={18} />
                    <div className={styles.detailContent}>
                      <span className={styles.detailLabel}>
                        {locale === "vi" ? "Diện tích:" : "Area:"}
                      </span>
                      <Description
                        as="span"
                        size="medium"
                        className={styles.detailValue}
                        translate="no"
                      >
                        {factory.area}
                      </Description>
                    </div>
                  </div>

                  <div className={styles.detailItem}>
                    <Factory className={styles.detailIcon} size={18} />
                    <div className={styles.detailContent}>
                      <span className={styles.detailLabel}>
                        {locale === "vi" ? "Công suất:" : "Capacity:"}
                      </span>
                      <Description
                        as="span"
                        size="medium"
                        className={styles.detailValue}
                        translate="no"
                      >
                        {factory.capacity}
                      </Description>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
